import {
  enumBGAreaLookup,
  enumBGOption,
  enumFillMode,
  enumUnitVal,
  kManualMargins
} from "../constants";
import { GetStdHeaderHeight } from "../utils/GetStdHeaderHeight";
import { GetStdFooterHeight } from "../utils/GetStdFooterHeight";
import { GetStdMargins } from "../utils/GetStdMargins";
import { IsOrgChartFile } from "../utils/IsOrgChartFile";
import { IsBackgroundFile } from "../utils/IsBackgroundFile";
import { QBackgroundArea } from "./QBackgroundArea";
import { QPageProps } from "./QPageProps";
import { QActiveBackground } from "./QActiveBackground";
import { QBGJournal } from "./QBGJournal";
import { Globals } from "./Globals";

export class QBackgroundMgr {
  constructor(wsMgr) {
    this.fActiveBackgroundArea = null;
    this.fBackgroundCache = {};
    this.fCurrentBackgroundName = null;
    this.fUID = null;
    this.fWSMgr = wsMgr;
  }

  GetUID() {
    if (this.fUID === null) {
      this.fUID = Math.floor(Date.now() / 1000);
    }
    return this.fUID;
  }

  InitializeActiveBackground() {
    var activeBackground = new QActiveBackground();
    activeBackground.InitActiveBackground(this);
    this.SetBackground(activeBackground);
  }

  LoadActiveBackground(backgroundName, theNodes, pageProps, loadFromNodes) {
    if (!this.fActiveBackgroundArea) {
      this.InitializeActiveBackground();
    }
    var activeBackground = this.fActiveBackgroundArea;
    if (loadFromNodes) {
      var backgroundArea = this.BuildBackgroundAreasFromNodes(theNodes);
      activeBackground.SetAreas(backgroundArea);
    }
    if (pageProps) {
      activeBackground.SetPageProps(pageProps);
    }

    if (IsBackgroundFile(backgroundName)) {
      this.fBackgroundCache[backgroundName] = activeBackground.Clone();
    }

    this.SetCurBackgroundName(backgroundName);
  }

  SetActiveBackground(background, backgroundName, pageProps, cacheIt = true) {
    if (!background) {
      this.InitializeActiveBackground();
    } else {
      this.SetBackground(background);
    }

    if (pageProps) {
      this.fActiveBackgroundArea.SetPageProps(pageProps);
    }

    if (backgroundName !== this.fCurrentBackgroundName) this.SetCurBackgroundName(backgroundName);

    if (IsBackgroundFile(this.fCurrentBackgroundName) && cacheIt) {
      this.fBackgroundCache[this.fCurrentBackgroundName] = background.Clone();
    }
  }

  SetBackground(background) {
    this.JournalBackgroundChange();
    this.fActiveBackgroundArea = background.Clone();
    this.fActiveBackgroundArea.InitActiveBackground(this);
  }

  ClearActiveBackground() {
    var pageProps = this.GetDefaultPageProps();
    this.fActiveBackgroundArea = null;
    this.BroadcastCurrentAreas();
    this.SetActiveBackground(null, null, pageProps);
    this.ReApplyBackground();
  }

  ReApplyBackground() {
    this.fWSMgr.ApplyBackground();
  }

  UpdateActiveBackground(backgroundAreas, pageProps) {
    var wsMgr = this.fWSMgr;
    wsMgr.StartCheckPoint();

    if (!this.fActiveBackgroundArea) {
      this.InitializeActiveBackground();
    }

    if (pageProps) {
      this.SetActivePageProps(pageProps);
    }

    if (backgroundAreas) {
      this.SetActiveBackgroundAreas(backgroundAreas);
      this.BroadcastCurrentAreas();
    }

    this.SignalActiveBackgroundChanged();

    wsMgr.EndCheckPoint();
  }

  BroadcastCurrentAreas() {
    var wsMgr = this.fWSMgr;
    var backgroundName = this.GetCurBackgroundName();
    var backgroundArea = this.fActiveBackgroundArea?.Clone();
    wsMgr.BroadcastTopic(`BackgroundUpdate_${backgroundName}`, backgroundArea?.GetAreas() ?? {});
    // debounce background application
    setTimeout(() => {
      wsMgr.ApplyBackground();
      wsMgr.PrepRenderWS();
      wsMgr.RenderWS();
    }, 100);
  }

  BuildBackgroundAreasFromNodes(nodes) {
    var layoutAreas = {};
    if (!nodes) return layoutAreas;
    for (var node of nodes) {
      var layout = node.GetLayout();
      var newLayout = layout.Clone({ includeId: true });
      if (newLayout.GetMarginMode() !== kManualMargins) newLayout.SetDefaultBackgroundMargins();
      var area = this.DetermineLayoutArea(layout);
      var existingArea = layoutAreas[area];
      if (!existingArea) {
        var backgroundArea = new QBackgroundArea();
        backgroundArea.InitBackgroundArea();
        backgroundArea.AddLayout(newLayout);
        layoutAreas[area] = backgroundArea;
      } else {
        existingArea.AddLayout(newLayout);
      }
    }
    return layoutAreas;
  }

  GetBackgroundNodes() {
    if (!this.fActiveBackgroundArea) return [];
    return this.fActiveBackgroundArea.GetBackgroundNodes();
  }

  ResetCurrentBackground() {
    this.fCurrentBackgroundName = null;
    this.fActiveBackgroundArea = null;
    this.fWSMgr?.BroadcastTopic("BackgroundNameChange", { name: "None" });
  }

  SignalActiveBackgroundChanged() {
    var wsMgr = this.fWSMgr;
    var doc = wsMgr?.GetDoc();
    var iWizData = doc?.GetIWizData();
    var mpOption = iWizData?.GetMasterOption();
    if (mpOption == enumBGOption.linked) {
      iWizData?.SetMasterOption(enumBGOption.custom);
    }
    wsMgr?.SetDirty(!doc?.IsServerChart() && mpOption === enumBGOption.custom);
    wsMgr?.SetBackgroundDirty(true);
  }

  LocateLayout(objId) {
    var areas = this.GetActiveBackgroundAreas();
    var layout = null;
    var index = null;
    for (var key in areas) {
      var bArea = areas[key];
      var layouts = bArea.GetLayouts();
      for (var i = 0; i < layouts.length; i++) {
        var curLayout = layouts[i];
        if (curLayout.GetDBObjID() === objId) {
          layout = curLayout;
          index = i;
          return { layout, index };
        }
      }
    }
    return { layout, index };
  }

  AddLayout(layout) {
    var backgroundClone = this.fActiveBackgroundArea.Clone();
    var newArea = backgroundClone.GetAreas();
    var areaKey = this.DetermineLayoutArea(layout);
    if (!newArea[areaKey]) {
      var customArea = new QBackgroundArea();
      customArea.InitBackgroundArea();
      customArea.AddLayout(layout);
      newArea[areaKey] = customArea;
    } else {
      newArea[areaKey].AddLayout(layout);
    }
    this.UpdateActiveBackground(newArea);
  }

  UpdateLayout(updateLayout) {
    var objId = updateLayout?.GetDBObjID();
    var { layout, index } = this.LocateLayout(objId);
    if (!layout) return;
    var areaKey = this.DetermineLayoutArea(layout);
    var backgroundClone = this.fActiveBackgroundArea.Clone();
    var newArea = backgroundClone.GetAreas();
    newArea[areaKey].RemoveLayoutAtIndex(index);
    newArea[areaKey].InsertLayout(updateLayout, index);
    this.UpdateActiveBackground(newArea);
  }

  RemoveLayout(objId) {
    var { layout, index } = this.LocateLayout(objId);
    if (!layout) return;
    var areaKey = this.DetermineLayoutArea(layout);
    var backgroundClone = this.fActiveBackgroundArea.Clone();
    var newArea = backgroundClone.GetAreas();
    newArea[areaKey].RemoveLayoutAtIndex(index);
    this.UpdateActiveBackground(newArea);
  }

  SetElementFocus(area, layoutIndex) {
    if (!this.fActiveBackgroundArea) return;
    this.fActiveBackgroundArea.currentElementFocus = { area, layoutIndex };
  }

  SelectBackgroundElement(id, clearPrior = true) {
    var wsMgr = this.fWSMgr;
    var canvas = wsMgr.fCanvasPanel;
    if (clearPrior) canvas.ClearSelection();
    if (!id) return;
    canvas.FocusObject({ dbObjId: id });
  }

  RetrieveCachedBackground(backgroundName) {
    return backgroundName != null ? this.fBackgroundCache[backgroundName] : null;
  }

  LoadBackgroundFromCache(backgroundName) {
    var cachedBackground = this.RetrieveCachedBackground(backgroundName);
    if (cachedBackground != null && cachedBackground?.GetAreas() != null) {
      var newBackground = cachedBackground.Clone();
      newBackground.InitActiveBackground(this);
      this.SetActiveBackground(newBackground, backgroundName);
    }
  }

  BustMasterCache(backgroundName) {
    if (backgroundName != null) {
      delete this.fBackgroundCache[backgroundName];
    }
  }

  SetCurBackgroundName(backgroundName = null) {
    var newBackgroundName;
    if (backgroundName == null || IsOrgChartFile(backgroundName)) {
      newBackgroundName = "None";
    } else if (IsBackgroundFile(backgroundName)) {
      newBackgroundName = backgroundName;
    } else if (!backgroundName.includes(".oc5m") && backgroundName != "None") {
      newBackgroundName = backgroundName + ".oc5m";
    } else newBackgroundName = backgroundName;
    this.fCurrentBackgroundName = newBackgroundName;
    this.fWSMgr.BroadcastTopic("BackgroundNameChange", { name: newBackgroundName });
  }

  GetCurBackgroundName() {
    return this.fCurrentBackgroundName;
  }

  IsViewAppliedBackground() {
    return this.fViewAppliedBackground;
  }

  SetIsViewAppliedBackground(viewApplied) {
    this.fViewAppliedBackground = viewApplied;
  }

  SetDefaultPubMaster(masterName) {
    this.fPubMaster = masterName;
  }

  GetPubMaster() {
    return this.fPubMaster;
  }

  SetActiveBackgroundAreas(newAreas) {
    this.JournalBackgroundChange();
    this.fActiveBackgroundArea.SetAreas(newAreas);
  }

  GetActiveBackgroundAreas() {
    return this.fActiveBackgroundArea?.GetAreas();
  }

  SetActivePageProps(pp) {
    this.JournalBackgroundChange();
    return this.fActiveBackgroundArea?.SetPageProps(pp);
  }

  GetActivePageProps() {
    return this.fActiveBackgroundArea?.GetPageProps();
  }

  SyncAreasWithDoc(doc) {
    this.fActiveBackgroundArea?.SyncAreasWithDoc(doc);
  }

  SyncBackgroundWithDoc(baseDoc, nextDoc) {
    var wsMgr = this.fWSMgr;
    if (nextDoc == null) {
      var isDirty = wsMgr.IsDocDirty();
      if (!isDirty) return;
    }

    var docMgr = wsMgr.GetDocMgr();
    var basepm = docMgr.GetPageMgr();
    var pageProps = basepm.GetPageProps();
    // if publishing we use base page props
    if (Globals._publishRef == null) {
      pageProps = this.GetActivePageProps() ?? pageProps;
    }

    var n = docMgr.GetNumDocuments();
    if (nextDoc == null) {
      for (var i = 0; i < n; i++) {
        var d = docMgr.GetIndexDoc(i);
        if (d != baseDoc) {
          var cp = d.GetCanvasRef();
          this.SyncAreasWithDoc(d);
          var mpNodes = this.GetBackgroundNodes();
          cp.ApplyBackgroundToCanvas(mpNodes, pageProps, d, true);
        }
      }
    } else {
      var cp = nextDoc.GetCanvasRef();
      this.SyncAreasWithDoc(nextDoc);
      var mpNodes = this.GetBackgroundNodes();
      cp.ApplyBackgroundToCanvas(mpNodes, pageProps, nextDoc, true);
    }
  }

  DetermineLayoutArea(layout) {
    if (!layout) return null;
    var horzArea = layout.GetChartHorzCentering();
    var vertArea = layout.GetChartVertCentering();
    return enumBGAreaLookup[`${horzArea}${vertArea}`] ?? "customPlacement";
  }

  LoadFromJSON(json) {
    var activeBackgroundJSON = json["activeBackgroundArea"];

    var activeBackground = new QActiveBackground();
    activeBackground.InitActiveBackground(this);
    if (activeBackgroundJSON) {
      activeBackground.LoadFromJSON(activeBackgroundJSON);
    }
    return activeBackground;
  }

  OutputToJSON() {
    var jsonResult = {};
    jsonResult["activeBackgroundArea"] = this.fActiveBackgroundArea.OutputToJSON();
    return jsonResult;
  }

  GetDefaultPageProps() {
    var wsMgr = this.fWSMgr;
    var pageMgr = wsMgr.GetPageMgr();

    var pp = new QPageProps();
    pp.fPageWidth = pageMgr.GetMinPageWidth();
    pp.fPageHeight = pageMgr.GetMinPageHeight();
    pp.fMargins = GetStdMargins();
    pp.fPageOrientation = pageMgr.GetPageOrientation();
    pp.fShowMargins = true;
    pp.fHeaderHeight = GetStdHeaderHeight(enumUnitVal.pixels);
    pp.fFooterHeight = GetStdFooterHeight(enumUnitVal.pixels);
    pp.fShowHeaderFooter = true;
    pp.fBackgroundColor = "#FFFFFF";
    pp.fBackgroundFillMode = enumFillMode.solidFill;

    return pp;
  }

  FetchBackground(backgroundName) {
    var cachedBackground = this.RetrieveCachedBackground(backgroundName);
    if (cachedBackground != null && cachedBackground["areas"]) {
      return cachedBackground;
    }

    var wsMgr = this.fWSMgr;
    var theDoc = wsMgr.GetDoc();
    var iWizData = theDoc.GetIWizData();
    var scm = wsMgr.GetSubChartMgr();
    var sc = scm.GetActiveSubChart();
    var scID = sc?.GetSubChartID();
    scm.SetLoadedStatus(scID, false);

    var loadBgRFC = wsMgr.DownloadBackground(iWizData, true, backgroundName);
    loadBgRFC.fSCID = scID;

    return null;
  }

  //==============================================================================
  // Undo/Redo Support
  StartJournal() {
    this.fBGJournal = new QBGJournal();
    this.fBGJournal.InitJournal(this);
  }

  JournalBackgroundChange() {
    if (this.fBGJournal && this.fActiveBackgroundArea)
      this.fBGJournal.BackgroundChanged(this.fActiveBackgroundArea);
  }

  JournalChanged() {
    if (this.fBGJournal) {
      this.fBGJournal.JournalChanged();
      this.fWSMgr.SetBackgroundDirty(true);
    }
  }

  ClearJournal() {
    this.fBGJournal = null;
  }

  GetJournal() {
    var j = this.fBGJournal;
    if (j && j.JournalChanged()) return j;
    else return null;
  }

  RestoreJournal(aJournal) {
    var updateCode = aJournal.GetUpdateCode();
    this.StartJournal();
    aJournal.Restore();

    return updateCode;
  }

  GainElementFocus(clearPrior = true) {
    if (!this.fActiveBackgroundArea) return;
    this.fActiveBackgroundArea.GainElementFocus(clearPrior);
  }
}
